/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react'
import { Dropdown, MenuItem } from 'react-bootstrap'

import authorization from 'live/services/authorization'
import i18n from 'live/services/i18n'

import {
    FilteredSearch,
    Scrollbars,
    IListItemError,
    EntityErrorModal,
    IError
} from 'plugin-codaf-risk-ext-bowtie/generic'

import { IBarrierTypes, IEditorBarrier, IEditorEscalation } from '../../BarrierEditor'
import { BarrierListContainer, ListCard } from './styles'

type IProps = {
    barrier: IEditorBarrier
    errors: IError[]
    handleEditBarrier?: (k: IEditorBarrier | IEditorEscalation, type: IBarrierTypes) => void
    onListDeleteClick: () => void
}

type IListItem = {
    type: IBarrierTypes
    name: string
    id: string | number
    onEdit: () => void
    onDelete: () => void
}

export const BarrierEditorList: React.FC<IProps> = ({ barrier, handleEditBarrier, onListDeleteClick, errors }) => {
    const [selectedType, setSelectedType] = useState<IBarrierTypes | 'ALL'>('ALL')
    const [sort, setSort] = useState<'ASC' | 'DESC'>(null)
    const [searchedString, setSearchedString] = useState<string>('')
    const [errorsModal, setErrorsModal] = useState(false)
    const bowtieAdmin = authorization.superAdmin || authorization.Permission.BOWTIE_ADMIN

    const getEntitiesCount = (bar: IEditorBarrier): [number, number] => {
        let barriersCount = 0
        let escalationsCount = 0

        if (bar) {
            if (bar.escalations) {
                for (const escalation of bar.escalations) {
                    escalationsCount += 1

                    if (escalation.barriers) {
                        for (const barrie of escalation.barriers) {
                            barriersCount += 1
                            const [barrierCountToAdd, escalationCountToAdd] = getEntitiesCount(barrie as IEditorBarrier)
                            barriersCount += barrierCountToAdd
                            escalationsCount += escalationCountToAdd
                        }
                    }
                }
            }
        }

        return [barriersCount, escalationsCount]
    }

    const handleEdit = (type: IBarrierTypes, data: IEditorBarrier | IEditorEscalation): void => {
        if (data === barrier) {
            handleEditBarrier(data, 'BARRIER')
        } else if (type === 'ESCALATIONFACTOR') {
            handleEditBarrier(data, 'ESCALATIONFACTOR')
        } else {
            handleEditBarrier(data, 'ESCALATIONBARRIER')
        }
    }

    const handleDelete = (
        type: 'BARRIER' | 'ESCALATIONFACTOR',
        data: IEditorBarrier | IEditorEscalation,
        fatherPointer: any
    ): void => {
        if (type === 'BARRIER') {
            const newArray = (fatherPointer.barriers as IEditorBarrier[]).filter(e => e !== data)
            fatherPointer.barriers = newArray
        } else if (type === 'ESCALATIONFACTOR') {
            const newArray = (fatherPointer.escalations as IEditorEscalation[]).filter(e => e !== data)
            fatherPointer.escalations = newArray
        }
        onListDeleteClick()
    }

    const handleSort = (a: IListItem, b: IListItem): number => {
        if (sort) {
            if (a.name < b.name) {
                return sort === 'ASC' ? -1 : 1
            }

            if (a.name > b.name) {
                return sort === 'ASC' ? 1 : -1
            }
        }

        return 0
    }

    const mountList = (bar: IEditorBarrier, father?: IEditorEscalation): IListItem[] => {
        let items: IListItem[] = []

        if (bar) {
            items.push({
                type: bar === barrier ? 'BARRIER' : 'ESCALATIONBARRIER',
                name: bar.name,
                id: bar.id,
                onEdit: () => handleEdit('BARRIER', bar),
                onDelete: () => father && handleDelete('BARRIER', bar, father)
            })

            if (bar.escalations) {
                for (const escalation of bar.escalations as IEditorEscalation[]) {
                    if (escalation) {
                        items.push({
                            type: 'ESCALATIONFACTOR',
                            name: escalation.name,
                            id: escalation.id,
                            onEdit: () => handleEdit('ESCALATIONFACTOR', escalation),
                            onDelete: () => handleDelete('ESCALATIONFACTOR', escalation, bar)
                        })

                        if (escalation.barriers) {
                            for (const barrie of escalation.barriers) {
                                if (barrie) {
                                    items = [...items, ...mountList(barrie as IEditorBarrier, escalation)]
                                }
                            }
                        }
                    }
                }
            }
        }

        return items
    }

    return (
        <BarrierListContainer>
            <div className="header">
                <div className="header-title">{i18n('LIST OF ENTITIES')}</div>
                <div className="header-subtitle">
                    {getEntitiesCount(barrier).reduce((a, b) => a + b, 0) + 1} {i18n('items')}
                </div>
                <button className="header-error-alert" onClick={() => errors.length && setErrorsModal(true)}>
                    <i className={`far fa-bell ${errors.length > 0 ? 'has-errors' : ''}`}></i>
                </button>
            </div>
            <div className="row-div">
                <FilteredSearch onSearch={input => setSearchedString(input)} keyword={searchedString} />
                <Dropdown pullRight id="sort-button">
                    <Dropdown.Toggle>
                        <i className="far fa-sort-alt" />
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                        <MenuItem active={!sort} onClick={() => setSort(null)}>
                            {i18n('Default')}
                        </MenuItem>
                        <MenuItem active={sort === 'ASC'} onClick={() => setSort('ASC')}>
                            {i18n('Ascending')}
                        </MenuItem>
                        <MenuItem active={sort === 'DESC'} onClick={() => setSort('DESC')}>
                            {i18n('Descending')}
                        </MenuItem>
                    </Dropdown.Menu>
                </Dropdown>
            </div>
            <div className="type-bar">
                <div className={selectedType === 'ALL' ? 'active' : ''} onClick={() => setSelectedType('ALL')}>
                    {i18n('All')}
                </div>
                <div className={selectedType === 'BARRIER' ? 'active' : ''} onClick={() => setSelectedType('BARRIER')}>
                    {i18n('Barrier')}
                </div>
                <div
                    className={selectedType === 'ESCALATIONFACTOR' ? 'active' : ''}
                    onClick={() => setSelectedType('ESCALATIONFACTOR')}
                >
                    {i18n('Escalation Factor')}
                </div>
            </div>
            <div className="barriers-escalation-list">
                <Scrollbars>
                    {mountList(barrier)
                        .sort(handleSort)
                        .filter(item => (selectedType === 'ALL' ? true : item.type.includes(selectedType)))
                        .filter(item => item.name?.toLowerCase().includes(searchedString.toLowerCase()))
                        .map(({ type, name, id, onEdit, onDelete }, idx) => {
                            const displayName =
                                type === 'ESCALATIONFACTOR'
                                    ? type.replace('ESCALATION', 'ESCALATION ').toUpperCase()
                                    : type.replace('ESCALATION', 'CONTROL ').toUpperCase()

                            return (
                                <ListCard key={`${idx}-${id}`} className="custom-card">
                                    <div className="main-row" onClick={onEdit}>
                                        <div className={`main-col custom-card-border ${type}`} />
                                        <div className="main-col info">
                                            <div className="row-card">
                                                <div className="title">{i18n(displayName)}</div>
                                                <div className="id">{id ? `#${id}` : ''}</div>
                                            </div>
                                            <div className="card-description">{name || '-'}</div>
                                        </div>
                                    </div>
                                    {bowtieAdmin && type !== 'BARRIER' && (
                                        <div className="delete-button" onClick={onDelete}>
                                            <i className="fal fa-trash-alt" />
                                        </div>
                                    )}
                                </ListCard>
                            )
                        })}
                </Scrollbars>
            </div>

            <EntityErrorModal
                onClose={() => setErrorsModal(false)}
                show={errorsModal}
                errorsList={errors.map(
                    (error): IListItemError => ({
                        type: error.type,
                        errors: error.errors,
                        name: error.name,
                        onEdit: () =>
                            handleEdit(error.type as IBarrierTypes, error.item as IEditorBarrier | IEditorEscalation)
                    })
                )}
            />
        </BarrierListContainer>
    )
}
